// src/components/resources/glossary/BenchmarksTable.tsx

import type { SegmentBenchmark } from '~/lib/resources/types/glossary/terms'
import { slugify } from '~/lib/utils/formatters'
import { resolveSourceUrl } from '~/lib/resources/data/glossary/source-urls'
import RiExternalLinkLine from '~/icons/remix/generated/RiExternalLinkLine'

interface BenchmarksTableProps {
  benchmarks: readonly SegmentBenchmark[]
  metricName?: string
  className?: string
}

/**
 * Renders industry-segment benchmark ranges for a metric. Designed to be a
 * scannable, Databox-style range table that appears below the main definition
 * on metric glossary pages. Server-renderable; no client interactivity.
 *
 * Responsive: on phones the fixed-width columns squish unreadably (the narrow
 * Median column collapses and breaks values like `3.8x` character-by-character),
 * so each segment renders as a stacked card below `md`; the table renders from
 * `md` up, where the columns have room.
 */
export function BenchmarksTable({
  benchmarks,
  metricName,
  className = ''
}: BenchmarksTableProps): JSX.Element | null {
  if (!benchmarks || benchmarks.length === 0) return null

  const headingId = `benchmarks-${slugify(metricName ?? 'industry', '-')}`
  // Sources come pre-normalized as a string[] from process-glossary.ts
  // (split on ';', trimmed, deduped). One canonical field, no fallbacks.
  const sources = Array.from(new Set(benchmarks.flatMap((b) => b.sources ?? [])))
  const rowKey = (b: SegmentBenchmark, i: number): string =>
    `${b.segment}-${(b.sources ?? [])[0] ?? ''}-${i}`

  return (
    <section
      aria-labelledby={headingId}
      className={`rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900 ${className}`}
    >
      <h2
        id={headingId}
        className="text-2xl font-semibold text-gray-900 dark:text-white"
      >
        Industry Benchmarks{metricName ? ` for ${metricName}` : ''}
      </h2>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Typical performance ranges by industry segment. Benchmarks vary by
        platform, audience maturity, and attribution window — treat these as
        starting points, not targets.
      </p>

      {/* Mobile (< md): stacked cards — readable without a horizontal scroll. */}
      <ul className="mt-6 space-y-3 md:hidden">
        {benchmarks.map((b, i) => (
          <li
            key={`card-${rowKey(b, i)}`}
            className="rounded-lg border border-gray-200 p-4 dark:border-gray-800"
          >
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{b.segment}</p>
            <dl className="mt-2 space-y-1 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-gray-500 dark:text-gray-400">Typical range</dt>
                <dd className="text-right font-medium text-indigo-600 dark:text-indigo-400">{b.range}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-gray-500 dark:text-gray-400">Median</dt>
                <dd className="text-right font-medium text-gray-700 dark:text-gray-300">{b.median ?? '—'}</dd>
              </div>
            </dl>
            {b.note && (
              <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">{b.note}</p>
            )}
          </li>
        ))}
      </ul>

      {/* md and up: the full table. `min-w` + `overflow-x-auto` guard the rare
          narrow-but-≥md container; Range/Median never wrap mid-value. */}
      <div className="mt-6 hidden overflow-x-auto md:block">
        <table className="w-full min-w-[34rem] table-fixed divide-y divide-gray-200 dark:divide-gray-800">
          <colgroup>
            <col className="w-[22%]" />
            <col className="w-[22%]" />
            <col className="w-[12%]" />
            <col className="w-[44%]" />
          </colgroup>
          <thead>
            <tr>
              <th
                scope="col"
                className="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                Segment
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                Typical Range
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                Median
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                Notes
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {benchmarks.map((b, i) => (
              <tr key={rowKey(b, i)}>
                <td className="break-words py-3 pr-4 align-top text-sm font-medium text-gray-900 dark:text-white">
                  {b.segment}
                </td>
                <td className="break-words px-4 py-3 align-top text-sm text-indigo-600 dark:text-indigo-400">
                  {b.range}
                </td>
                <td className="break-words px-4 py-3 align-top text-sm text-gray-700 dark:text-gray-300">
                  {b.median ?? '—'}
                </td>
                <td className="break-words px-4 py-3 align-top text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  {b.note ?? ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sources.length > 0 && (
        // Source citations: recognized publishers render as nofollow links
        // to the publisher's canonical landing page (via the SOURCE_URLS
        // map); unrecognized publisher strings render as plain text so we
        // never ship a broken link to a citation. All external links get
        // rel="nofollow noopener noreferrer" + target="_blank" — nofollow
        // because these are third-party reference citations, not editorial
        // endorsements, and we don't want to pass PageRank to every
        // benchmark publisher we cite.
        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          <span className="font-medium text-gray-600 dark:text-gray-300">Sources:</span>{' '}
          {sources.map((source, i) => {
            const url = resolveSourceUrl(source)
            const isLast = i === sources.length - 1
            const separator = isLast ? '' : ', '
            return (
              <span key={source}>
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-indigo-600 underline decoration-indigo-200 underline-offset-2 hover:text-indigo-700 hover:decoration-indigo-400 dark:text-indigo-400 dark:decoration-indigo-700/60 dark:hover:text-indigo-300"
                  >
                    {source}
                    <RiExternalLinkLine
                      aria-hidden="true"
                      className="size-3 opacity-70"
                    />
                  </a>
                ) : (
                  <span>{source}</span>
                )}
                {separator}
              </span>
            )
          })}
        </p>
      )}
    </section>
  )
}

export default BenchmarksTable
